/**
 * Alert Component
 * Reusable alert component for notifications and messages
 * Integrated with the theme system for consistent styling
 */

import React from 'react';
import { useSafeThemeClasses } from '@/shared/theme';

interface AlertProps {
  type?: 'info' | 'success' | 'warning' | 'error';
  title?: string;
  message: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({
  type = 'info',
  title,
  message,
  dismissible = false,
  onDismiss,
  icon,
  className = '',
}) => {
  const theme = useSafeThemeClasses();
  
  const typeClasses = {
    info: theme.alertInfo,
    success: theme.alertSuccess,
    warning: theme.alertWarning,
    error: theme.alertError,
  };

  const defaultIcons = {
    info: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
    ),
    success: (
      <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    ),
    error: (
      <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  const classes = [
    'alert flex items-start gap-3 p-4 rounded-lg',
    typeClasses[type],
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {icon || defaultIcons[type]}
      <div className="flex-1">
        {title && <div className={theme.textHeading}>{title}</div>}
        <div className={theme.textSecondary}>{message}</div>
      </div>
      {dismissible && onDismiss && (
        <button
          className={`w-6 h-6 rounded-full flex items-center justify-center ${theme.buttonGhost} ${theme.buttonTextGhost} hover:opacity-75 transition-opacity`}
          onClick={onDismiss}
          aria-label="Dismiss alert"
        >
          ✕
        </button>
      )}
    </div>
  );
};

export default Alert;
